/**
 * Mock data for the redesigned Residents page.
 * ~20 realistic anonymized records across AL and IL types,
 * with varied device assignments, groups, and statuses.
 */

export const AREA_TYPES = {
  AL: 'Assisted Living',
  IL: 'Independent Living',
  MC: 'Memory Care',
};

export const STATUS = {
  ACTIVE: 'Active',
  MOVED_OUT: 'Moved Out',
  PENDING: 'Pending Move-In',
};

export const AREAS = [
  { area_id: 1, area_name: 'Room 101', area_type_name: 'AL', area_type_description: 'Assisted Living' },
  { area_id: 2, area_name: 'Room 102', area_type_name: 'AL', area_type_description: 'Assisted Living' },
  { area_id: 3, area_name: 'Room 103', area_type_name: 'AL', area_type_description: 'Assisted Living' },
  { area_id: 4, area_name: 'Suite 201', area_type_name: 'IL', area_type_description: 'Independent Living' },
  { area_id: 5, area_name: 'Suite 202', area_type_name: 'IL', area_type_description: 'Independent Living' },
  { area_id: 6, area_name: 'Suite 203', area_type_name: 'IL', area_type_description: 'Independent Living' },
  { area_id: 7, area_name: 'Room 301', area_type_name: 'MC', area_type_description: 'Memory Care' },
  { area_id: 8, area_name: 'Room 302', area_type_name: 'MC', area_type_description: 'Memory Care' },
  { area_id: 9, area_name: 'Room 104', area_type_name: 'AL', area_type_description: 'Assisted Living' },
  { area_id: 10, area_name: 'Suite 204', area_type_name: 'IL', area_type_description: 'Independent Living' },
  { area_id: 11, area_name: 'Room 105', area_type_name: 'AL', area_type_description: 'Assisted Living' },
  { area_id: 12, area_name: 'Room 303', area_type_name: 'MC', area_type_description: 'Memory Care' },
];

export const GROUPS = [
  { resident_group_id: 1, resident_group_name: 'Fall Risk' },
  { resident_group_id: 2, resident_group_name: 'High Risk' },
  { resident_group_id: 3, resident_group_name: 'Diabetic' },
  { resident_group_id: 4, resident_group_name: 'Wandering Risk' },
  { resident_group_id: 5, resident_group_name: 'Oxygen Dependent' },
  { resident_group_id: 6, resident_group_name: 'Hospice' },
];

export const GROUP_CATEGORIES = [
  { resident_group_category_id: 1, resident_group_category_name: 'Safety' },
  { resident_group_category_id: 2, resident_group_category_name: 'Medical' },
  { resident_group_category_id: 3, resident_group_category_name: 'Care Program' },
  { resident_group_category_id: 4, resident_group_category_name: 'Social' },
];

export const MOCK_GROUPS = [
  {
    resident_group_id: 1,
    resident_group_name: 'Fall Risk',
    resident_description: 'Residents identified as having elevated fall risk requiring additional monitoring and fall prevention protocols.',
    resident_group_category_id: 1,
    resident_group_category_name: 'Safety',
    members: [
      { resident_id: 1, resident_name: 'Margaret Anderson', area_name: 'Room 101', area_type_name: 'AL' },
      { resident_id: 4, resident_name: 'James Davis', area_name: 'Room 301', area_type_name: 'MC' },
      { resident_id: 8, resident_name: 'Thomas Hughes', area_name: 'Room 104', area_type_name: 'AL' },
      { resident_id: 12, resident_name: 'Charles Martin', area_name: 'Room 303', area_type_name: 'MC' },
      { resident_id: 15, resident_name: 'Mary Parker', area_name: 'Suite 201', area_type_name: 'IL' },
      { resident_id: 19, resident_name: 'Virginia Thompson', area_name: 'Room 104', area_type_name: 'AL' },
    ],
  },
  {
    resident_group_id: 2,
    resident_group_name: 'High Risk',
    resident_description: 'Residents requiring heightened clinical oversight due to multiple comorbidities or acute conditions.',
    resident_group_category_id: 2,
    resident_group_category_name: 'Medical',
    members: [
      { resident_id: 2, resident_name: 'Robert Bennett', area_name: 'Room 102', area_type_name: 'AL' },
      { resident_id: 6, resident_name: 'William Foster', area_name: 'Room 103', area_type_name: 'AL' },
      { resident_id: 11, resident_name: 'Barbara Lewis', area_name: 'Room 105', area_type_name: 'AL' },
      { resident_id: 18, resident_name: 'Frank Stevens', area_name: 'Room 103', area_type_name: 'AL' },
    ],
  },
  {
    resident_group_id: 3,
    resident_group_name: 'Diabetic',
    resident_description: 'Residents with diabetes requiring blood sugar monitoring and dietary management.',
    resident_group_category_id: 2,
    resident_group_category_name: 'Medical',
    members: [
      { resident_id: 1, resident_name: 'Margaret Anderson', area_name: 'Room 101', area_type_name: 'AL' },
      { resident_id: 9, resident_name: 'Patricia Johnson', area_name: 'Suite 203', area_type_name: 'IL' },
      { resident_id: 13, resident_name: 'Susan Nelson', area_name: 'Room 101', area_type_name: 'AL' },
      { resident_id: 19, resident_name: 'Virginia Thompson', area_name: 'Room 104', area_type_name: 'AL' },
    ],
  },
  {
    resident_group_id: 4,
    resident_group_name: 'Wandering Risk',
    resident_description: 'Memory care residents with elopement risk requiring wander management technology and protocols.',
    resident_group_category_id: 1,
    resident_group_category_name: 'Safety',
    members: [
      { resident_id: 4, resident_name: 'James Davis', area_name: 'Room 301', area_type_name: 'MC' },
      { resident_id: 7, resident_name: 'Helen Garcia', area_name: 'Room 302', area_type_name: 'MC' },
      { resident_id: 12, resident_name: 'Charles Martin', area_name: 'Room 303', area_type_name: 'MC' },
      { resident_id: 16, resident_name: 'Edward Quinn', area_name: 'Room 301', area_type_name: 'MC' },
      { resident_id: 20, resident_name: 'Harold Walker', area_name: 'Room 302', area_type_name: 'MC' },
    ],
  },
  {
    resident_group_id: 5,
    resident_group_name: 'Oxygen Dependent',
    resident_description: 'Residents on supplemental oxygen therapy requiring equipment checks and respiratory monitoring.',
    resident_group_category_id: 2,
    resident_group_category_name: 'Medical',
    members: [
      { resident_id: 6, resident_name: 'William Foster', area_name: 'Room 103', area_type_name: 'AL' },
      { resident_id: 12, resident_name: 'Charles Martin', area_name: 'Room 303', area_type_name: 'MC' },
      { resident_id: 18, resident_name: 'Frank Stevens', area_name: 'Room 103', area_type_name: 'AL' },
    ],
  },
  {
    resident_group_id: 6,
    resident_group_name: 'Hospice',
    resident_description: 'Residents enrolled in hospice care program with comfort-focused care plans.',
    resident_group_category_id: 3,
    resident_group_category_name: 'Care Program',
    members: [
      { resident_id: 11, resident_name: 'Barbara Lewis', area_name: 'Room 105', area_type_name: 'AL' },
      { resident_id: 20, resident_name: 'Harold Walker', area_name: 'Room 302', area_type_name: 'MC' },
    ],
  },
];

export const AVAILABLE_DEVICES = [
  { device_id: 101, device_name: 'Pendant A-001', manufacture_id: 1, manufacture_uuid: 'INV-2847561', inovonics_model_name: 'EchoStream' },
  { device_id: 102, device_name: 'Pendant A-002', manufacture_id: 1, manufacture_uuid: 'INV-2847562', inovonics_model_name: 'EchoStream' },
  { device_id: 103, device_name: 'Pendant A-003', manufacture_id: 1, manufacture_uuid: 'INV-2847563', inovonics_model_name: 'EchoStream' },
  { device_id: 104, device_name: 'Wristband B-010', manufacture_id: 4, manufacture_uuid: 'PAL-1001', inovonics_model_name: 'PalBand' },
  { device_id: 105, device_name: 'Wristband B-011', manufacture_id: 4, manufacture_uuid: 'PAL-1002', inovonics_model_name: 'PalBand' },
  { device_id: 106, device_name: 'Motion Sensor C-020', manufacture_id: 1, manufacture_uuid: 'INV-3910001', inovonics_model_name: 'EN1245' },
  { device_id: 107, device_name: 'Pendant A-004', manufacture_id: 1, manufacture_uuid: 'INV-2847564', inovonics_model_name: 'EchoStream' },
  { device_id: 108, device_name: 'Wristband B-012', manufacture_id: 4, manufacture_uuid: 'PAL-1003', inovonics_model_name: 'PalBand' },
  { device_id: 109, device_name: 'Bed Sensor D-030', manufacture_id: 1, manufacture_uuid: 'INV-5500010', inovonics_model_name: 'EN1252' },
  { device_id: 110, device_name: 'Pendant A-005', manufacture_id: 1, manufacture_uuid: 'INV-2847565', inovonics_model_name: 'EchoStream' },
];

/** Generates a past datetime string offset by daysAgo from now */
function daysAgoISO(daysAgo) {
  const d = new Date();
  d.setDate(d.getDate() - daysAgo);
  d.setHours(Math.floor(Math.random() * 12) + 6, Math.floor(Math.random() * 60), 0, 0);
  return d.toISOString();
}

export const MOCK_RESIDENTS = [
  {
    resident_id: 1,
    resident_first_name: 'Margaret',
    resident_last_name: 'Anderson',
    resident_nickname: 'Maggie',
    area_id: 1,
    area_name: 'Room 101',
    area_type_name: 'AL',
    area_type_description: 'Assisted Living',
    resident_groups: [
      { resident_group_id: 1, resident_group_name: 'Fall Risk' },
      { resident_group_id: 3, resident_group_name: 'Diabetic' },
    ],
    resident_phone: '555-201-1001',
    resident_email: 'margaret.a@family.com',
    ehr_level_of_care: 'Level 2',
    devices: [
      { device_id: 101, device_name: 'Pendant A-001', manufacture_id: 1, manufacture_uuid: 'INV-2847561' },
    ],
    check_in_time: daysAgoISO(0),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 2,
    resident_first_name: 'Robert',
    resident_last_name: 'Bennett',
    resident_nickname: 'Bob',
    area_id: 2,
    area_name: 'Room 102',
    area_type_name: 'AL',
    area_type_description: 'Assisted Living',
    resident_groups: [
      { resident_group_id: 2, resident_group_name: 'High Risk' },
    ],
    resident_phone: '555-201-1002',
    resident_email: 'rbennett@email.com',
    ehr_level_of_care: 'Level 3',
    devices: [
      { device_id: 102, device_name: 'Pendant A-002', manufacture_id: 1, manufacture_uuid: 'INV-2847562' },
      { device_id: 104, device_name: 'Wristband B-010', manufacture_id: 4, manufacture_uuid: 'PAL-1001' },
    ],
    check_in_time: daysAgoISO(1),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 3,
    resident_first_name: 'Dorothy',
    resident_last_name: 'Clark',
    resident_nickname: '',
    area_id: 4,
    area_name: 'Suite 201',
    area_type_name: 'IL',
    area_type_description: 'Independent Living',
    resident_groups: [],
    resident_phone: '555-201-1003',
    resident_email: 'dorothy.clark@mail.com',
    ehr_level_of_care: '',
    devices: [],
    check_in_time: daysAgoISO(3),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 4,
    resident_first_name: 'James',
    resident_last_name: 'Davis',
    resident_nickname: 'Jim',
    area_id: 7,
    area_name: 'Room 301',
    area_type_name: 'MC',
    area_type_description: 'Memory Care',
    resident_groups: [
      { resident_group_id: 4, resident_group_name: 'Wandering Risk' },
      { resident_group_id: 1, resident_group_name: 'Fall Risk' },
    ],
    resident_phone: '555-201-1004',
    resident_email: '',
    ehr_level_of_care: 'Level 4',
    devices: [
      { device_id: 103, device_name: 'Pendant A-003', manufacture_id: 1, manufacture_uuid: 'INV-2847563' },
      { device_id: 105, device_name: 'Wristband B-011', manufacture_id: 4, manufacture_uuid: 'PAL-1002' },
      { device_id: 109, device_name: 'Bed Sensor D-030', manufacture_id: 1, manufacture_uuid: 'INV-5500010' },
    ],
    check_in_time: daysAgoISO(0),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 5,
    resident_first_name: 'Eleanor',
    resident_last_name: 'Evans',
    resident_nickname: 'Ellie',
    area_id: 5,
    area_name: 'Suite 202',
    area_type_name: 'IL',
    area_type_description: 'Independent Living',
    resident_groups: [],
    resident_phone: '555-201-1005',
    resident_email: 'ellie.evans@family.net',
    ehr_level_of_care: '',
    devices: [
      { device_id: 107, device_name: 'Pendant A-004', manufacture_id: 1, manufacture_uuid: 'INV-2847564' },
    ],
    check_in_time: daysAgoISO(7),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 6,
    resident_first_name: 'William',
    resident_last_name: 'Foster',
    resident_nickname: 'Bill',
    area_id: 3,
    area_name: 'Room 103',
    area_type_name: 'AL',
    area_type_description: 'Assisted Living',
    resident_groups: [
      { resident_group_id: 5, resident_group_name: 'Oxygen Dependent' },
      { resident_group_id: 2, resident_group_name: 'High Risk' },
    ],
    resident_phone: '555-201-1006',
    resident_email: 'wfoster@email.com',
    ehr_level_of_care: 'Level 3',
    devices: [
      { device_id: 108, device_name: 'Wristband B-012', manufacture_id: 4, manufacture_uuid: 'PAL-1003' },
    ],
    check_in_time: daysAgoISO(2),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 7,
    resident_first_name: 'Helen',
    resident_last_name: 'Garcia',
    resident_nickname: '',
    area_id: 8,
    area_name: 'Room 302',
    area_type_name: 'MC',
    area_type_description: 'Memory Care',
    resident_groups: [
      { resident_group_id: 4, resident_group_name: 'Wandering Risk' },
    ],
    resident_phone: '555-201-1007',
    resident_email: '',
    ehr_level_of_care: 'Level 4',
    devices: [
      { device_id: 110, device_name: 'Pendant A-005', manufacture_id: 1, manufacture_uuid: 'INV-2847565' },
      { device_id: 106, device_name: 'Motion Sensor C-020', manufacture_id: 1, manufacture_uuid: 'INV-3910001' },
    ],
    check_in_time: daysAgoISO(1),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 8,
    resident_first_name: 'Thomas',
    resident_last_name: 'Hughes',
    resident_nickname: 'Tom',
    area_id: 9,
    area_name: 'Room 104',
    area_type_name: 'AL',
    area_type_description: 'Assisted Living',
    resident_groups: [
      { resident_group_id: 1, resident_group_name: 'Fall Risk' },
    ],
    resident_phone: '555-201-1008',
    resident_email: 'tom.hughes@family.com',
    ehr_level_of_care: 'Level 2',
    devices: [],
    check_in_time: daysAgoISO(5),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 9,
    resident_first_name: 'Patricia',
    resident_last_name: 'Johnson',
    resident_nickname: 'Pat',
    area_id: 6,
    area_name: 'Suite 203',
    area_type_name: 'IL',
    area_type_description: 'Independent Living',
    resident_groups: [
      { resident_group_id: 3, resident_group_name: 'Diabetic' },
    ],
    resident_phone: '555-201-1009',
    resident_email: 'patjohnson@mail.com',
    ehr_level_of_care: '',
    devices: [
      { device_id: 101, device_name: 'Pendant A-001', manufacture_id: 1, manufacture_uuid: 'INV-2847561' },
    ],
    check_in_time: daysAgoISO(0),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 10,
    resident_first_name: 'Richard',
    resident_last_name: 'Kelly',
    resident_nickname: 'Rick',
    area_id: 10,
    area_name: 'Suite 204',
    area_type_name: 'IL',
    area_type_description: 'Independent Living',
    resident_groups: [],
    resident_phone: '555-201-1010',
    resident_email: 'rick.kelly@email.com',
    ehr_level_of_care: '',
    devices: [],
    check_in_time: daysAgoISO(14),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 11,
    resident_first_name: 'Barbara',
    resident_last_name: 'Lewis',
    resident_nickname: 'Barb',
    area_id: 11,
    area_name: 'Room 105',
    area_type_name: 'AL',
    area_type_description: 'Assisted Living',
    resident_groups: [
      { resident_group_id: 6, resident_group_name: 'Hospice' },
      { resident_group_id: 2, resident_group_name: 'High Risk' },
    ],
    resident_phone: '555-201-1011',
    resident_email: '',
    ehr_level_of_care: 'Level 5',
    devices: [
      { device_id: 102, device_name: 'Pendant A-002', manufacture_id: 1, manufacture_uuid: 'INV-2847562' },
      { device_id: 109, device_name: 'Bed Sensor D-030', manufacture_id: 1, manufacture_uuid: 'INV-5500010' },
    ],
    check_in_time: daysAgoISO(0),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 12,
    resident_first_name: 'Charles',
    resident_last_name: 'Martin',
    resident_nickname: 'Charlie',
    area_id: 12,
    area_name: 'Room 303',
    area_type_name: 'MC',
    area_type_description: 'Memory Care',
    resident_groups: [
      { resident_group_id: 4, resident_group_name: 'Wandering Risk' },
      { resident_group_id: 1, resident_group_name: 'Fall Risk' },
      { resident_group_id: 5, resident_group_name: 'Oxygen Dependent' },
    ],
    resident_phone: '555-201-1012',
    resident_email: '',
    ehr_level_of_care: 'Level 4',
    devices: [
      { device_id: 103, device_name: 'Pendant A-003', manufacture_id: 1, manufacture_uuid: 'INV-2847563' },
    ],
    check_in_time: daysAgoISO(1),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 13,
    resident_first_name: 'Susan',
    resident_last_name: 'Nelson',
    resident_nickname: 'Sue',
    area_id: 1,
    area_name: 'Room 101',
    area_type_name: 'AL',
    area_type_description: 'Assisted Living',
    resident_groups: [
      { resident_group_id: 3, resident_group_name: 'Diabetic' },
    ],
    resident_phone: '555-201-1013',
    resident_email: 'sue.nelson@family.com',
    ehr_level_of_care: 'Level 2',
    devices: [
      { device_id: 107, device_name: 'Pendant A-004', manufacture_id: 1, manufacture_uuid: 'INV-2847564' },
    ],
    check_in_time: daysAgoISO(4),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 14,
    resident_first_name: 'George',
    resident_last_name: 'Owens',
    resident_nickname: '',
    area_id: 2,
    area_name: 'Room 102',
    area_type_name: 'AL',
    area_type_description: 'Assisted Living',
    resident_groups: [],
    resident_phone: '555-201-1014',
    resident_email: 'g.owens@email.com',
    ehr_level_of_care: '',
    devices: [],
    check_in_time: daysAgoISO(10),
    is_active: false,
    status: 'Moved Out',
  },
  {
    resident_id: 15,
    resident_first_name: 'Mary',
    resident_last_name: 'Parker',
    resident_nickname: '',
    area_id: 4,
    area_name: 'Suite 201',
    area_type_name: 'IL',
    area_type_description: 'Independent Living',
    resident_groups: [
      { resident_group_id: 1, resident_group_name: 'Fall Risk' },
    ],
    resident_phone: '555-201-1015',
    resident_email: 'mary.parker@mail.com',
    ehr_level_of_care: '',
    devices: [
      { device_id: 110, device_name: 'Pendant A-005', manufacture_id: 1, manufacture_uuid: 'INV-2847565' },
    ],
    check_in_time: daysAgoISO(2),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 16,
    resident_first_name: 'Edward',
    resident_last_name: 'Quinn',
    resident_nickname: 'Ed',
    area_id: 7,
    area_name: 'Room 301',
    area_type_name: 'MC',
    area_type_description: 'Memory Care',
    resident_groups: [
      { resident_group_id: 4, resident_group_name: 'Wandering Risk' },
    ],
    resident_phone: '555-201-1016',
    resident_email: '',
    ehr_level_of_care: 'Level 3',
    devices: [
      { device_id: 104, device_name: 'Wristband B-010', manufacture_id: 4, manufacture_uuid: 'PAL-1001' },
    ],
    check_in_time: daysAgoISO(0),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 17,
    resident_first_name: 'Ruth',
    resident_last_name: 'Roberts',
    resident_nickname: '',
    area_id: 5,
    area_name: 'Suite 202',
    area_type_name: 'IL',
    area_type_description: 'Independent Living',
    resident_groups: [],
    resident_phone: '555-201-1017',
    resident_email: 'ruth.r@family.net',
    ehr_level_of_care: '',
    devices: [],
    check_in_time: daysAgoISO(21),
    is_active: false,
    status: 'Moved Out',
  },
  {
    resident_id: 18,
    resident_first_name: 'Frank',
    resident_last_name: 'Stevens',
    resident_nickname: '',
    area_id: 3,
    area_name: 'Room 103',
    area_type_name: 'AL',
    area_type_description: 'Assisted Living',
    resident_groups: [
      { resident_group_id: 2, resident_group_name: 'High Risk' },
      { resident_group_id: 5, resident_group_name: 'Oxygen Dependent' },
    ],
    resident_phone: '555-201-1018',
    resident_email: 'frank.stevens@email.com',
    ehr_level_of_care: 'Level 3',
    devices: [
      { device_id: 105, device_name: 'Wristband B-011', manufacture_id: 4, manufacture_uuid: 'PAL-1002' },
      { device_id: 106, device_name: 'Motion Sensor C-020', manufacture_id: 1, manufacture_uuid: 'INV-3910001' },
    ],
    check_in_time: daysAgoISO(1),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 19,
    resident_first_name: 'Virginia',
    resident_last_name: 'Thompson',
    resident_nickname: 'Ginny',
    area_id: 9,
    area_name: 'Room 104',
    area_type_name: 'AL',
    area_type_description: 'Assisted Living',
    resident_groups: [
      { resident_group_id: 1, resident_group_name: 'Fall Risk' },
      { resident_group_id: 3, resident_group_name: 'Diabetic' },
    ],
    resident_phone: '555-201-1019',
    resident_email: 'ginny.t@family.com',
    ehr_level_of_care: 'Level 2',
    devices: [
      { device_id: 108, device_name: 'Wristband B-012', manufacture_id: 4, manufacture_uuid: 'PAL-1003' },
    ],
    check_in_time: daysAgoISO(3),
    is_active: true,
    status: 'Active',
  },
  {
    resident_id: 20,
    resident_first_name: 'Harold',
    resident_last_name: 'Walker',
    resident_nickname: 'Harry',
    area_id: 8,
    area_name: 'Room 302',
    area_type_name: 'MC',
    area_type_description: 'Memory Care',
    resident_groups: [
      { resident_group_id: 4, resident_group_name: 'Wandering Risk' },
      { resident_group_id: 6, resident_group_name: 'Hospice' },
    ],
    resident_phone: '555-201-1020',
    resident_email: '',
    ehr_level_of_care: 'Level 5',
    devices: [
      { device_id: 110, device_name: 'Pendant A-005', manufacture_id: 1, manufacture_uuid: 'INV-2847565' },
      { device_id: 109, device_name: 'Bed Sensor D-030', manufacture_id: 1, manufacture_uuid: 'INV-5500010' },
    ],
    check_in_time: daysAgoISO(0),
    is_active: true,
    status: 'Active',
  },
];

export const CAMPUSES = [
  { campus_id: 1, campus_name: 'Sunrise Senior Living' },
  { campus_id: 2, campus_name: 'Maplewood Care Center' },
  { campus_id: 3, campus_name: 'Oakwood Residence' },
];
